import { useState } from 'react';
import { ArrowLeft, ChevronRight, Search, XCircle, Loader2 } from 'lucide-react';
import { useRecordClassifications } from '../hooks/useRecordClassifications';
import type { ClassificationEntry } from '../hooks/useRecordClassifications';
import { useFunctionClassifications } from '../hooks/useFunctionClassifications';

interface PickerOption {
  key: string;
  label: string;
  sublabel?: string;
  onClick: () => void;
}

interface BasePickerProps {
  title: string;
  levelLabels: string[];
  level: 1 | 2 | 3;
  options: PickerOption[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  breadcrumbs: { label: string; onClick: () => void }[];
  onBack: (() => void) | null;
  onClose: () => void;
}

function PickerModal({
  title, levelLabels, level, options, loading,
  search, onSearchChange, breadcrumbs, onBack, onClose,
}: BasePickerProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            {onBack && (
              <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
            )}
            <div>
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{levelLabels[level - 1]}</p>
            </div>
          </div>
          <button onClick={onClose}>
            <XCircle className="w-6 h-6 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        {breadcrumbs.length > 0 && (
          <div className="px-6 pt-4 flex items-center gap-1 text-sm shrink-0 flex-wrap">
            {breadcrumbs.map((bc, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="w-3 h-3 text-gray-400" />}
                <button
                  onClick={bc.onClick}
                  className="text-emerald-600 hover:text-emerald-800 font-medium truncate max-w-[200px]"
                  title={bc.label}
                >
                  {bc.label}
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="px-6 pt-4 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${levelLabels[level - 1].toLowerCase()}...`}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
              autoFocus
            />
          </div>
        </div>

        <div className="p-4 flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
            </div>
          ) : options.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              {search ? 'No matches found.' : 'No options available.'}
            </div>
          ) : (
            <div className="space-y-1">
              {options.map(opt => (
                <button
                  key={opt.key}
                  className="w-full text-left p-3 rounded-lg hover:bg-emerald-50 border border-transparent hover:border-emerald-200 transition-colors group/opt"
                  onClick={opt.onClick}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 group-hover/opt:text-emerald-800">{opt.label}</span>
                    {level < 3 && <ChevronRight className="w-4 h-4 text-gray-300 group-hover/opt:text-emerald-500" />}
                  </div>
                  {opt.sublabel && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{opt.sublabel}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


interface RecordClassificationPickerProps {
  onSelect: (entry: ClassificationEntry) => void;
  onClose: () => void;
  initialLevel?: 1 | 2 | 3;
  initialLevel1?: string | null;
  initialLevel2?: string | null;
}

export function RecordClassificationPicker({ onSelect, onClose, initialLevel = 1, initialLevel1 = null, initialLevel2 = null }: RecordClassificationPickerProps) {
  const { hierarchy, loading } = useRecordClassifications();
  const [level, setLevel] = useState<1 | 2 | 3>(initialLevel);
  const [selectedLevel1, setSelectedLevel1] = useState<string | null>(initialLevel1);
  const [selectedLevel2, setSelectedLevel2] = useState<string | null>(initialLevel2);
  const [search, setSearch] = useState('');

  const searchLower = search.toLowerCase();
  const levelLabels = ['Level 1 — Function', 'Level 2 — Activity', 'Level 3 — Record Type'];

  let options: PickerOption[] = [];

  if (level === 1) {
    options = Object.keys(hierarchy)
      .sort()
      .filter(l1 => !searchLower || l1.toLowerCase().includes(searchLower))
      .map(l1 => ({
        key: l1,
        label: l1,
        sublabel: `${Object.keys(hierarchy[l1]).length} activities`,
        onClick: () => { setSelectedLevel1(l1); setSelectedLevel2(null); setLevel(2); setSearch(''); },
      }));
  } else if (level === 2 && selectedLevel1 && hierarchy[selectedLevel1]) {
    options = Object.keys(hierarchy[selectedLevel1])
      .sort()
      .filter(l2 => !searchLower || l2.toLowerCase().includes(searchLower))
      .map(l2 => ({
        key: l2,
        label: l2,
        sublabel: `${hierarchy[selectedLevel1][l2].length} record types`,
        onClick: () => { setSelectedLevel2(l2); setLevel(3); setSearch(''); },
      }));
  } else if (level === 3 && selectedLevel1 && selectedLevel2 && hierarchy[selectedLevel1]?.[selectedLevel2]) {
    options = hierarchy[selectedLevel1][selectedLevel2]
      .sort((a, b) => a.level_3.name.localeCompare(b.level_3.name))
      .filter(entry => !searchLower || entry.level_3.name.toLowerCase().includes(searchLower) || entry.level_3.description.toLowerCase().includes(searchLower))
      .map(entry => ({
        key: entry.classification_id,
        label: entry.level_3.name,
        sublabel: entry.level_3.description.length > 120 ? entry.level_3.description.slice(0, 120) + '...' : entry.level_3.description,
        onClick: () => onSelect(entry),
      }));
  }

  const breadcrumbs: { label: string; onClick: () => void }[] = [];
  if (selectedLevel1) {
    breadcrumbs.push({ label: selectedLevel1, onClick: () => { setSelectedLevel2(null); setLevel(2); setSearch(''); } });
  }
  if (selectedLevel2) {
    breadcrumbs.push({ label: selectedLevel2, onClick: () => {} });
  }

  const handleBack = level > 1 ? () => {
    setSearch('');
    if (level === 3) { setSelectedLevel2(null); setLevel(2); }
    else if (level === 2) { setSelectedLevel1(null); setLevel(1); }
  } : null;

  return (
    <PickerModal
      title="Select Record Classification"
      levelLabels={levelLabels}
      level={level}
      options={options}
      loading={loading}
      search={search}
      onSearchChange={setSearch}
      breadcrumbs={breadcrumbs}
      onBack={handleBack}
      onClose={onClose}
    />
  );
}


export interface FunctionClassificationSelection {
  classification_id: string;
  domain: { domain_name: string; domain_key: string };
  subdomain: { subdomain_name: string; subdomain_key: string };
  function: { function_name: string; function_key: string };
}

interface FunctionClassificationPickerProps {
  onSelect: (selection: FunctionClassificationSelection) => void;
  onClose: () => void;
  initialLevel?: 1 | 2 | 3;
  initialDomain?: string | null;
  initialSubdomain?: string | null;
}

export function FunctionClassificationPicker({ onSelect, onClose, initialLevel = 1, initialDomain = null, initialSubdomain = null }: FunctionClassificationPickerProps) {
  const { hierarchy, loading } = useFunctionClassifications();
  const [level, setLevel] = useState<1 | 2 | 3>(initialLevel);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(initialDomain);
  const [selectedSubdomain, setSelectedSubdomain] = useState<string | null>(initialSubdomain);
  const [search, setSearch] = useState('');

  const searchLower = search.toLowerCase();
  const levelLabels = ['Domain', 'Subdomain', 'Function'];

  let options: PickerOption[] = [];

  if (level === 1) {
    options = Object.keys(hierarchy)
      .sort((a, b) => hierarchy[a].domain.localeCompare(hierarchy[b].domain))
      .filter(dk => !searchLower || hierarchy[dk].domain.toLowerCase().includes(searchLower))
      .map(dk => ({
        key: dk,
        label: hierarchy[dk].domain,
        sublabel: hierarchy[dk].description.length > 120 ? hierarchy[dk].description.slice(0, 120) + '...' : hierarchy[dk].description,
        onClick: () => { setSelectedDomain(dk); setSelectedSubdomain(null); setLevel(2); setSearch(''); },
      }));
  } else if (level === 2 && selectedDomain && hierarchy[selectedDomain]) {
    options = hierarchy[selectedDomain].subdomains
      .filter(s => !searchLower || s.name.toLowerCase().includes(searchLower))
      .map(s => ({
        key: s.key,
        label: s.name,
        sublabel: `${s.functions.length} functions`,
        onClick: () => { setSelectedSubdomain(s.key); setLevel(3); setSearch(''); },
      }));
  } else if (level === 3 && selectedDomain && selectedSubdomain && hierarchy[selectedDomain]) {
    const subdomain = hierarchy[selectedDomain].subdomains.find(s => s.key === selectedSubdomain);
    if (subdomain) {
      options = subdomain.functions
        .filter(f => !searchLower || f.name.toLowerCase().includes(searchLower))
        .map(f => ({
          key: f.key,
          label: f.name,
          onClick: () => onSelect({
            classification_id: f.classification_id,
            domain: { domain_name: hierarchy[selectedDomain].domain, domain_key: selectedDomain },
            subdomain: { subdomain_name: subdomain.name, subdomain_key: subdomain.key },
            function: { function_name: f.name, function_key: f.key },
          }),
        }));
    }
  }

  const breadcrumbs: { label: string; onClick: () => void }[] = [];
  if (selectedDomain && hierarchy[selectedDomain]) {
    breadcrumbs.push({ label: hierarchy[selectedDomain].domain, onClick: () => { setSelectedSubdomain(null); setLevel(2); setSearch(''); } });
  }
  if (selectedSubdomain && selectedDomain && hierarchy[selectedDomain]) {
    const sub = hierarchy[selectedDomain].subdomains.find(s => s.key === selectedSubdomain);
    if (sub) breadcrumbs.push({ label: sub.name, onClick: () => {} });
  }

  const handleBack = level > 1 ? () => {
    setSearch('');
    if (level === 3) { setSelectedSubdomain(null); setLevel(2); }
    else if (level === 2) { setSelectedDomain(null); setLevel(1); }
  } : null;

  return (
    <PickerModal
      title="Select Function Classification"
      levelLabels={levelLabels}
      level={level}
      options={options}
      loading={loading}
      search={search}
      onSearchChange={setSearch}
      breadcrumbs={breadcrumbs}
      onBack={handleBack}
      onClose={onClose}
    />
  );
}
